import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Spinner } from '@4zen/ui';
import { AppShell } from '@/components/AppShell';
import { useAuth } from '@/stores/auth';
import { LoginPage } from '@/pages/LoginPage';
import { MyDayPage } from '@/features/smart-flow/MyDayPage';

const CalendarPage = lazy(() => import('@/features/tasks/CalendarPage'));
const TaskDetailPage = lazy(() => import('@/features/tasks/TaskDetailPage'));
const ClientsPage = lazy(() => import('@/features/clients/ClientsPage'));
const ClientDetailPage = lazy(() => import('@/features/clients/ClientDetailPage'));
const InboxPage = lazy(() => import('@/features/comms/InboxPage'));
const ChannelsPage = lazy(() => import('@/features/comms/ChannelsPage'));
const AssistantPage = lazy(() => import('@/features/ai/AssistantPage'));
const SalesPage = lazy(() => import('@/features/sales/SalesPage'));
const ScrumPage = lazy(() => import('@/features/scrum/ScrumPage'));
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'));
const RetentionPage = lazy(() => import('@/features/retention/RetentionPage'));
const AuditPage = lazy(() => import('@/features/admin/AuditPage'));
const SettingsPage = lazy(() => import('@/features/admin/SettingsPage'));
const ConflictsPage = lazy(() => import('@/features/admin/ConflictsPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));

function FullPageSpinner() {
  return (
    <div className="grid h-full place-items-center">
      <Spinner className="h-6 w-6 text-accent" />
    </div>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          path="/"
          element={
            <Suspense fallback={<FullPageSpinner />}>
              <MyDayPage />
            </Suspense>
          }
        />
        {(
          [
            ['/calendar', CalendarPage],
            ['/tasks/:taskId', TaskDetailPage],
            ['/clients', ClientsPage],
            ['/clients/:clientId', ClientDetailPage],
            ['/inbox', InboxPage],
            ['/assistant', AssistantPage],
            ['/sales', SalesPage],
            ['/scrum', ScrumPage],
            ['/analytics', AnalyticsPage],
            ['/retention', RetentionPage],
            ['/audit', AuditPage],
            ['/settings', SettingsPage],
            ['/settings/conflicts', ConflictsPage],
            ['/settings/channels', ChannelsPage],
            ['/search', SearchPage],
            ['/notifications', NotificationsPage],
          ] as const
        ).map(([path, Component]) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<FullPageSpinner />}>
                <Component />
              </Suspense>
            }
          />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
